import React, { useState, useEffect } from "react";
import Header from "./Header";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import { useParams } from "react-router-dom";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ZMCMConveyanceDetails = () => {
  const [profile, setProfile] = useState(null);
  const [details, setDetails] = useState([]);
  const [visitRows, setVisitRows] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [conveyanceStatus, setConveyanceStatus] = useState("N");
  const { id } = useParams();
  const currentMonth = new Date().toLocaleString("en-US", { month: "long" });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");

        // 1️⃣ Fetch Profile
        const resProfile = await fetch(`${API_BASE_URL}profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const profileResult = await resProfile.json();

        if (!profileResult.status || !profileResult.profile) {
          setMessage("Unable to load profile");
          setLoading(false);
          return;
        }

        const empCode = profileResult.profile.emp_code;
        setProfile(profileResult.profile);

        const resStatus = await fetch(`${API_BASE_URL}check_status`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const statusResult = await resStatus.json();

        if (statusResult.status && statusResult.data?.conveyance_status) {
          setConveyanceStatus(statusResult.data.conveyance_status); // Y or N
        }

        // 2️⃣ Fetch Master Details
        const resMaster = await fetch(`${API_BASE_URL}getcmzmdetails/${empCode}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const masterResult = await resMaster.json();

        let masterData;
        if (masterResult.status && masterResult.data.status === "success" && Array.isArray(masterResult.data.data)) {
          masterData = masterResult.data.data[0];
          setDetails([{
            id: masterData.id,
            emp_code: masterData.emp_code,
            role: masterData.role,
            duration_type: masterData.duration_type,
            "1_15_cnt": masterData["1_15_cnt"],
            "16_30_cnt": masterData["16_30_cnt"],
            "1_30_cnt": masterData["1_30_cnt"],
            amount: parseFloat(masterData.amount),
            branches: masterData.branches_list.map(b => b.name).join(", "),
            created_on: masterData.created_on,
            branches_list: masterData.branches_list
          }]);
        } else {
          setMessage(masterResult.data?.message || "No ZM/CM details found");
        }

        // 3️⃣ Fetch Visit Details
        const resVisits = await fetch(`${API_BASE_URL}getmyvisitdetails1/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const visitResult = await resVisits.json();

        const existingVisits = (visitResult.status && Array.isArray(visitResult.data))
          ? visitResult.data.map((v, index) => ({
            id: v.id,
            visit_no: index + 1,
            branch_id: v.branch,
            branch_name: v.branch, // replace with name if available
            date: v.visit_date,
            time: v.visit_time,
            day: v.visit_day,
            purpose: v.purpose,
            remarks: v.remarks,
            amount: parseFloat(v.amount),
            attachment: v.attachment || null,
          }))
          : [];

        const totalAmount = masterData?.amount || 0;
        const totalBranches = masterData?.branches_list?.length || 1;
        let visitsPerBranch = 1;
        let amountPerVisit = totalAmount / totalBranches;
        if (masterData?.role === "cluster_manager") {
          visitsPerBranch = 2;
          amountPerVisit = totalAmount / (totalBranches * 2);
        } else {
          visitsPerBranch = 1;
          amountPerVisit = totalAmount / totalBranches;
        }

        // Generate remaining rows per branch (2 per branch)
        let allVisits = [];
        if (masterData?.branches_list) {
          masterData.branches_list.forEach((branch) => {
            const filled = existingVisits.filter(v => v.branch_id === branch.id);
            filled.forEach(v => {
              // override amount to per-branch amount
              allVisits.push({ ...v, amount: amountPerVisit });
            });

            const remaining = 2 - filled.length;
            for (let i = 0; i < remaining; i++) {
              allVisits.push({
                visit_no: allVisits.length + 1,
                branch_id: branch.id,
                branch_name: branch.name,
                date: "",
                day: "",
                purpose: "",
                remarks: "",
                amount: amountPerVisit,
                attachment: null
              });
            }
          });
        }

        setVisitRows(allVisits);

      } catch (err) {
        console.error(err);
        setMessage("Server error while loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Update date & day
  const handleDateChange = (index, value) => {
    const updated = [...visitRows];
    updated[index].date = value;
    updated[index].day = value ? new Date(value).toLocaleDateString("en-US", { weekday: "short" }) : "";
    setVisitRows(updated);
  };

  const handleTimeChange = (index, value) => {
    const updated = [...visitRows];
    updated[index].time = value;
    setVisitRows(updated);
  };

  const handleInputChange = (index, field, value) => {
    const updated = [...visitRows];
    updated[index][field] = value;
    setVisitRows(updated);
  };

  const handleFileChange = (index, file) => {
    const updated = [...visitRows];
    updated[index].attachment = file;
    setVisitRows(updated);
  };

  // Update single row
  const handleUpdateRow = async (index) => {
    if (!profile) {
      alert("Profile not loaded");
      return;
    }

    const v = visitRows[index];

    // 🔴 Validation
    if (!v.date) {
      alert(`Please select Date for Visit ${v.visit_no} - ${v.branch_name}`);
      return;
    }

    if (!v.purpose.trim()) {
      alert(`Please enter Purpose for Visit ${v.visit_no} - ${v.branch_name}`);
      return;
    }

    if (!v.amount || v.amount <= 0) {
      alert(`Invalid amount for Visit ${v.visit_no} - ${v.branch_name}`);
      return;
    }

    // ✅ Build FormData
    const formData = new FormData();
    formData.append("emp_code", profile.emp_code);
    formData.append("employee_name", profile.comp_name);
    formData.append("role", details[0]?.role || "");
    formData.append("duration", details[0]?.duration_type || "");

    formData.append("visit_no", v.visit_no);
    formData.append("branch_id", v.branch_id);
    formData.append("branch_name", v.branch_name);
    formData.append("date", v.date);
    formData.append("time", v.time || "");
    formData.append("day", v.day || "");
    formData.append("purpose", v.purpose);
    formData.append("remarks", v.remarks || "");
    formData.append("amount", v.amount);

    if (v.attachment) {
      formData.append("attachment", v.attachment);
    }

    try {
      const token = localStorage.getItem("authToken");

      // 🔑 IMPORTANT: decide URL
      const url = v.id
        ? `${API_BASE_URL}update_zmcm_conveyance/${v.id}`   // UPDATE
        : `${API_BASE_URL}update_zmcm_conveyance`;         // INSERT

      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const result = await res.json();

      if (result.status) {
        alert(
          v.id
            ? `✅ Visit ${v.visit_no} updated successfully!`
            : `✅ Visit ${v.visit_no} added successfully!`
        );

        // 🔥 If INSERT, save returned ID into state
        if (!v.id && result.id) {
          setVisitRows((prev) => {
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              id: result.id,
            };
            return updated;
          });
        }
      } else {
        alert(result.message || "❌ Submission failed");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Server error while saving");
    }
  };


  const handleSubmit = async () => {
    if (!profile) {
      alert("Profile not loaded");
      return;
    }

    // 🔔 ALERT CONFIRMATION
    const confirmed = window.confirm(
      "Once you send for approval, you will NOT be able to edit this conveyance.\n\nDo you want to continue?"
    );

    if (!confirmed) {
      return; // user clicked Cancel
    }

    try {
      const token = localStorage.getItem("authToken");

      const res = await fetch(`${API_BASE_URL}update_zmcm_conveyance_status`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          emp_code: profile.emp_code,
          status: "SENT_FOR_APPROVAL",
        }),
      });

      const result = await res.json();

      if (result.status === true || result.status === "success") {
        alert("🚀 Conveyance sent for approval successfully!");
        setIsSubmitted(true); // 🔒 lock editing
      } else {
        alert(result.message || "❌ Failed to update status");
      }
    } catch (err) {
      console.error(err);
      alert("❌ Server error while updating status");
    }
  };



  if (loading) return <h3 style={{ padding: 20, color: "#6ca0dc" }}>Loading...</h3>;

  return (
    <div className="hold-transition sidebar-mini layout-fixed" style={{ background: "#f4f7fb", fontSize: '12px' }}>
      <div className="wrapper">
        <Header />
        <Sidebar />
        <div className="content-wrapper" style={{ padding: "25px" }}>
          <h2 style={{ color: "#6ca0dc", fontWeight: 800, fontSize: "24px", marginBottom: "12px" }}>
            Conveyance Details
          </h2>

          {message && <div style={{ padding: "12px", background: "#fdeeee", borderLeft: "4px solid #d87a7a", borderRadius: "8px", marginBottom: "18px", color: "#8b3f3f" }}>{message}</div>}

          {profile && <div style={{ background: "#ffffffee", padding: "15px 20px", borderRadius: "12px", border: "1px solid #e2e8f5", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            <b style={{ color: "#557a95" }}>Employee:</b> {profile.comp_name} ({profile.emp_code})
          </div>}

          {/* <h3 style={{ color: "#557a95", marginBottom: "10px" }}>Master Summary</h3> */}
          {details.length > 0 && <div style={{ background: "#ffffffee", padding: "20px", borderRadius: "12px", border: "1px solid #d9e3f1", boxShadow: "0 2px 10px rgba(0,0,0,0.05)", marginBottom: "25px" }}>
            {details.map((row, idx) => (
              <div key={idx} style={{ display: "flex", flexWrap: "wrap", gap: "22px", fontSize: "14px", color: "#425b70" }}>
                <span><b>ID:</b> {row.id}</span>
                <span><b>Role:</b> {row.role}</span>
                <span><b>Duration:</b> {row.duration_type} days</span>
                {/* <span><b>1–15:</b> {row["1_15_cnt"]}</span>
                <span><b>16–30:</b> {row["16_30_cnt"]}</span> */}
                <span><b>Total Branches:</b> {row["1_30_cnt"]}</span>
                <span><b>Amount:</b> ₹{row.amount}</span>
                <span><b>Branches:</b> {row.branches}</span>
                <span><b>Created:</b> {row.created_on}</span>
                <span style={{ background: "#ffefc7", color: "#a06400", padding: "4px 10px", borderRadius: "8px", fontWeight: "700" }}>
                  <b>Current Month:</b> {currentMonth}
                </span>
              </div>
            ))}
          </div>}

          <h3 style={{ color: "#557a95", fontSize: "15px", marginBottom: "8px" }}>
            Visit Records ({visitRows.length} Visits)
          </h3>

          <div style={{ maxHeight: "440px", overflowY: "auto", background: "#ffffffee", borderRadius: "10px", border: "1px solid #d9e3f1", padding: "10px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}>
            {visitRows.map((row, index) => (
              <div key={index} style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "12px", background: "#fff", padding: "8px 10px", marginBottom: "8px", borderRadius: "8px", border: "1px solid #e3ecf7", fontSize: "12.5px" }}>
                <span style={{ width: "60px" }}><b>V:</b> {row.visit_no}</span>
                <span style={{ width: "70px" }}><b>ID:</b> {row.branch_id}</span>
                <span style={{ width: "180px", whiteSpace: "nowrap" }}><b>Branch:</b> {row.branch_name}</span>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <b style={{ marginRight: "4px" }}>Date:</b>
                  <input type="date" value={row.date} onChange={(e) => handleDateChange(index, e.target.value)} style={{ fontSize: "12px", padding: "3px", height: "26px", borderRadius: "4px", border: "1px solid #9ad1d4" }} />
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <b style={{ marginRight: "4px" }}>Time:</b>
                  <input type="time" value={row.time} onChange={(e) => handleTimeChange(index, e.target.value)} style={{ fontSize: "12px", padding: "3px", height: "26px", borderRadius: "4px", border: "1px solid #9ad1d4" }} />
                </div>

                <span style={{ width: "65px" }}><b>Day:</b> {row.day}</span>
                <input type="text" placeholder="Purpose" value={row.purpose} onChange={(e) => handleInputChange(index, "purpose", e.target.value)} style={{ width: "120px", fontSize: "12px", padding: "4px", height: "26px", borderRadius: "4px", border: "1px solid #9ad1d4" }} />
                <input type="text" placeholder="Remarks" value={row.remarks} onChange={(e) => handleInputChange(index, "remarks", e.target.value)} style={{ width: "120px", fontSize: "12px", padding: "4px", height: "26px", borderRadius: "4px", border: "1px solid #9ad1d4" }} />

                <span
                  style={{
                    background: "#6ca0dc",
                    color: "#fff",
                    padding: "3px 10px",
                    borderRadius: "14px",
                    fontSize: "12px",
                    fontWeight: "600"
                  }}
                >
                  ₹{Number(row.amount).toFixed(2)}
                </span>


                {row.attachment && (
                  <div style={{ width: "30px", height: "30px" }}>
                    <img
                      src={
                        typeof row.attachment === "string"
                          ? `${API_BASE_URL.replace("/api/", "")}/assets/uploads/${row.attachment}` // existing uploaded file
                          : URL.createObjectURL(row.attachment) // new file selected
                      }
                      alt="attachment"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: "6px",
                        border: "1px solid #d3d3d3",
                      }}
                    />
                  </div>
                )}


                <input type="file" accept="image/*,application/pdf" onChange={(e) => handleFileChange(index, e.target.files[0])} style={{ fontSize: "11px" }} />

                {conveyanceStatus !== "Y" && (
                  <button
                    onClick={() => handleUpdateRow(index)}
                    style={{
                      marginLeft: "8px",
                      padding: "4px 8px",
                      background: "#28a745",
                      color: "#fff",
                      borderRadius: "4px",
                      border: "none",
                      cursor: "pointer",
                    }}
                  >
                    Update
                  </button>
                )}
              </div>
            ))}

          </div>
          {conveyanceStatus !== "Y" && (
            <button
              onClick={() => handleSubmit("approval")}
              style={{
                background: "linear-gradient(135deg, #34a853, #4caf50)",
                color: "#fff",
                padding: "12px 36px",
                fontSize: "15px",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                fontWeight: "700",
                boxShadow: "0 4px 12px rgba(52,168,83,0.4)",
                marginTop: "15px",
              }}
            >
              🚀 Send for Approval
            </button>
          )}
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default ZMCMConveyanceDetails;
